import { ExcelDatatableService } from './../../services/excel-datatable/excel-datatable.service';
import { ExcelService } from './../../../../services/excel/excel.service';
import { DataTable, Excel, Sort } from './../../interfaces/datatable.interface';
import { Component, Input, OnInit } from '@angular/core';
import { isObservable } from 'rxjs';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-datatable-excel-descargar',
  templateUrl: './datatable-excel-descargar.component.html',
  styleUrls: ['./datatable-excel-descargar.component.scss']
})
export class DatatableExcelDescargarComponent implements OnInit {

  @Input() datatable: DataTable;
  @Input() filtros = {};
  @Input() sorts: Sort[] = [];

  BATCH_SIZE = 500;
  PROMISE_BATCH = 5;
  loading = false;
  excelLinkDownload = false;

  page = 1;
  lastPage = -1;
  lastTotal = -1;
  total = 0;

  cancelarDescarga = false;

  EXCEL_INTERVAL_TIME_MS = 5000;
  porcentajeExcelBackend = 0
  carga: number = 0;

  constructor(private excelService: ExcelService, private excelDatatableService: ExcelDatatableService) { }

  ngOnInit(): void {
  }

  async descargarExcel(excel: Excel) {
    if (excel.tipo === 'snapshot' || excel.tipo === 'snapshot-total') {
      const headers = excel.tipo === 'snapshot' ? this.getSnapshotHeaders() : this.getFullSnapshotHeaders();
      const getRowFn = excel.tipo === 'snapshot' ? this.getSnapshotRow : this.getSnapshotFullRow;

      const excelLink = this.datatable.linkExcel;
      if (excelLink) {
        this.descargarExcelPorBackend(excelLink, headers, excel.nombreDescarga)
      } else {
        await this.generarYGuardarExcel(excel, headers, getRowFn);
      }
    } else if (excel.tipo === 'custom') {
      await this.generarYGuardarExcel(excel, excel.customHeaders(), excel.customRows);
    }
  }

  descargarExcelPorBackend(excelLink, headers, nombreDescarga: string) {
    this.excelLinkDownload = true;
    let callStatus = true
    this.porcentajeExcelBackend = 0;
    this.carga = 0;
    excelLink(this.filtros, this.sorts, headers).subscribe(response => {
      const excelStreamId = response.id;
      const intervalId = setInterval(() => {
        if (callStatus) {
          callStatus = false;
          this.excelDatatableService.estadoExcel(excelStreamId).subscribe(resp => {
            this.carga++;
            callStatus = true;
            this.porcentajeExcelBackend = resp.percentage;
            if (resp.finished && this.excelLinkDownload) {
              this.excelDatatableService.descargarExcel(excelStreamId, nombreDescarga).subscribe(response => {
                clearInterval(intervalId);
                this.excelLinkDownload = false;
                const blob = new Blob([response.body], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
                FileSaver.saveAs(blob, nombreDescarga + '_export_' + new Date().getTime() + '.xlsx');
              })
            }
          }, (_) => callStatus = true)
        }
      }, this.EXCEL_INTERVAL_TIME_MS)
    });
  }

  async generarYGuardarExcel(excel: Excel, headers, getRowFn) {
    this.loading = true;
    this.cancelarDescarga = false;
    if (!this.datatable.servicio) {
      this.generarYGuardarExcelSinServicio(excel, headers, getRowFn);
      this.stopLoading();
      return;
    }
    const data = [headers];
    this.page = 1;
    let fixedFiltros = { ...this.filtros };
    let fixedSorts = [...this.sorts];

    const addServiceResponseToExcelRowData = (response) => {
      const responseData = this.datatable.data(response);
      responseData.forEach(item => {
        const rowData = getRowFn(this.datatable, item);
        if (rowData && rowData.length > 0 && rowData[0] instanceof Array) {
          rowData.forEach(row => data.push(row));
        } else {
          data.push(rowData);
        }
      });
      this.total += responseData.length;
    }

    const firstResponse = await this.datatable.servicio(fixedFiltros, fixedSorts, this.page, this.BATCH_SIZE).toPromise();
    addServiceResponseToExcelRowData(firstResponse);

    this.lastTotal = -1;
    this.total = 0;
    if (this.datatable.paginacion.usarTotal !== false) {
      const totalResponse = this.datatable.paginacion.total(firstResponse);
      const total = isObservable(totalResponse) ? (await totalResponse.toPromise()) : totalResponse;
      this.lastPage = Math.ceil(total / this.BATCH_SIZE);
    } else {
      this.lastPage = 100000000;
    }
    this.page++;

    let promises = [];
    while (this.page <= this.lastPage && this.lastTotal !== this.total && !this.cancelarDescarga) {
      promises.push(this.datatable.servicio(fixedFiltros, fixedSorts, this.page, this.BATCH_SIZE).toPromise());

      if (promises.length >= this.PROMISE_BATCH || this.page >= this.lastPage) {
        this.lastTotal = this.total;
        const responses = await Promise.all(promises);
        responses.forEach(response => addServiceResponseToExcelRowData(response));
        promises = [];
      }

      this.page++;
    }

    if (promises.length > 0) {
      const responses = await Promise.all(promises);
      responses.forEach(response => addServiceResponseToExcelRowData(response));
    }

    this.stopLoading();
    if (!this.cancelarDescarga) {
      this.excelService.exportAsExcelFile(data, excel.nombreDescarga, { skipHeader: true });
    } else {
      this.cancelarDescarga = false;
    }
  }

  generarYGuardarExcelSinServicio(excel: Excel, headers, getRowFn) {
    const data = [headers];
    const responseData = this.datatable.data(null);
    responseData.forEach(item => {
      const rowData = getRowFn(this.datatable, item);
      if (rowData && rowData.length > 0 && rowData[0] instanceof Array) {
        rowData.forEach(row => data.push(row));
      } else {
        data.push(rowData);
      }
    });
    this.excelService.exportAsExcelFile(data, excel.nombreDescarga, { skipHeader: true });
  }

  onCancelarDescargaClick() {
    this.cancelarDescarga = true;
  }

  getSnapshotHeaders(): string[] {
    return this.datatable.columnas.filter(c => c.mostrar).map(c => c.nombre);
  }

  getFullSnapshotHeaders(): string[] {
    return this.datatable.columnas.map(c => c.nombre);
  }

  getSnapshotRow(datatable: DataTable, item): (string | number | Date)[] {
    return datatable.columnas.filter(c => c.mostrar).map(c => {
      let valor = ''
      if (c.valorExcel) {
        const val = c.valorExcel(item);
        valor = val || val === '0' || val === 0 ? val.toString() : '';
      } else {
        const val = c.valor(item);
        valor = val || val === '0' || val === 0 ? val.toString() : '';
      }
      if (valor == '0') {
        return 0;
      }
      if (/^-?\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
        const tokens = valor.split('/');
        const date = new Date(parseInt(tokens[2], 10), parseInt(tokens[1], 10) - 1, parseInt(tokens[0], 10));
        date.setHours(4, 0, 0, 1);
        return date;
      }
      if (/^-?\d+\.\d+$/.test(valor)) {
        return parseFloat(valor);
      }
      if (/^-?\d+,\d+$/.test(valor)) {
        return parseFloat(valor.toString().replace(',', '.'));
      }
      if (/^-?\d+$/.test(valor)) {
        return parseInt(valor);
      }
      return valor;
    });
  }

  getSnapshotFullRow(datatable: DataTable, item): (string | number | Date)[] {
    return datatable.columnas.map(c => {
      let valor = ''
      if (c.valorExcel) {
        const val = c.valorExcel(item);
        valor = val || val === '0' || val === 0 ? val.toString() : '';
      } else {
        const val = c.valor(item);
        valor = val || val === '0' || val === 0 ? val.toString() : '';
      }
      if (valor == '0') {
        return 0;
      }
      if (/^-?\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
        const tokens = valor.split('/');
        const date = new Date(parseInt(tokens[2], 10), parseInt(tokens[1], 10) - 1, parseInt(tokens[0], 10));
        date.setHours(4, 0, 0, 1);
        return date;
      }
      if (/^-?\d+\.\d+$/.test(valor)) {
        return parseFloat(valor);
      }
      if (/^-?\d+,\d+$/.test(valor)) {
        return parseFloat(valor.toString().replace(',', '.'));
      }
      if (/^-?\d+$/.test(valor)) {
        return parseInt(valor);
      }
      return valor;
    });
  }

  stopLoading() {
    setTimeout(() => this.loading = false, 100);
  }

  getPercentage() {
    if (this.datatable.paginacion.usarTotal === false) {
      return 'Espere... (' + this.total + ')';
    } else if (this.lastPage === -1 || this.lastPage === 0) {
      return '0%';
    } else if (this.page >= this.lastPage) {
      return '100%';
    } else {
      return Math.round(this.page * 100 / this.lastPage) + '%';
    }
  }

}
